<template>
  <Drawer
    class="stop-event-drawer"
    :visible="open"
    position="bottom"
    modal
    :show-close-icon="false"
    :pt="DRAWER_PASS_THROUGH"
    @update:visible="(visible) => !visible && emit('close')"
  >
    <div class="stop-event-drawer__inner">
      <div class="stop-event-drawer__head">
        <span class="stop-event-drawer__head-icon"><i class="pi pi-stop-circle" /></span>
        <span class="stop-event-drawer__head-title">{{ confirming ? 'End this event?' : 'Halt Event' }}</span>
        <span class="stop-event-drawer__spacer" />
        <Button class="stop-event-drawer__dismiss" icon="pi pi-times" severity="secondary" @click="emit('close')" />
      </div>

      <template v-if="!confirming">
        <span class="stop-event-drawer__question">How do you want to stop the event?</span>

        <div class="stop-event-drawer__choices">
          <Button
            v-for="option in CHOICES"
            :key="option.value"
            class="stop-event-drawer__choice"
            :class="[
              `stop-event-drawer__choice--${option.value}`,
              { 'stop-event-drawer__choice--selected': choice === option.value },
            ]"
            @click="choice = option.value"
          >
            <i :class="`pi pi-${option.icon}`" class="stop-event-drawer__choice-icon" />
            <span class="stop-event-drawer__choice-title">{{ option.title }}</span>
            <span class="stop-event-drawer__choice-subtitle">{{ option.subtitle }}</span>
          </Button>
        </div>

        <div class="stop-event-drawer__reason">
          <label class="stop-event-drawer__reason-label" for="stop-event-drawer-reason">
            Reason <span class="stop-event-drawer__reason-optional">(optional)</span>
          </label>
          <Textarea
            id="stop-event-drawer-reason"
            v-model="reason"
            class="stop-event-drawer__textarea"
            rows="3"
            placeholder="e.g. Network outage — pausing until restored."
          />
        </div>

        <div class="stop-event-drawer__actions">
          <Button class="stop-event-drawer__cancel" label="Cancel" @click="emit('close')" />
          <Button
            class="stop-event-drawer__primary"
            :class="`stop-event-drawer__primary--${choice}`"
            :disabled="pausing"
            :label="primaryLabel"
            @click="advance"
          />
        </div>
      </template>

      <template v-else>
        <span class="stop-event-drawer__warning">
          Ending finalizes every asset's outcome and closes the sale. Items still under reserve are recorded as
          <strong>No Sale</strong>. This cannot be undone.
        </span>

        <div v-if="reason" class="stop-event-drawer__reason-echo">
          <span class="stop-event-drawer__reason-echo-label">Reason</span>{{ reason }}
        </div>

        <span class="stop-event-drawer__spacer" />

        <div class="stop-event-drawer__actions">
          <Button class="stop-event-drawer__cancel" label="Back" :disabled="ending" @click="confirming = false" />
          <Button
            class="stop-event-drawer__primary stop-event-drawer__primary--end"
            :disabled="ending"
            @click="advance"
          >
            <i class="pi pi-stop-circle" />{{ ending ? 'Ending…' : 'End event' }}
          </Button>
        </div>
      </template>
    </div>
  </Drawer>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Drawer from 'primevue/drawer';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';

// The full-height sheet behind Halt Event, offering the two ways to stop a sale.
//
// Pause and End are not two settings of one action. Pause is recoverable and
// the sale picks up where it left off; End finalizes every outstanding lot and
// cannot be undone — which is why End takes a SECOND confirmation, on its own
// screen, restating what will happen to lots still under reserve.
//
// It called `pauseEvent` and `stopEvent` itself. It emits them with the reason
// now, and takes `pausing` / `ending` back so the buttons lock while the call
// is out. The reason is optional here and required by the routes, so supplying
// a fallback is the application's job — it is the one that knows which route it
// is about to call.
defineOptions({ name: 'StopEventDrawer' });

const props = defineProps({
  open: { type: Boolean, default: false },
  // A pause is in flight.
  pausing: { type: Boolean, default: false },
  // An end is in flight.
  ending: { type: Boolean, default: false },
});

const emit = defineEmits(['pause', 'end', 'close']);

const CHOICES = [
  {
    value: 'pause',
    icon: 'pause',
    title: 'Pause event',
    subtitle: 'Temporarily halt bidding. You can resume where you left off.',
  },
  {
    value: 'end',
    icon: 'stop-circle',
    title: 'End event',
    subtitle: "Finalize all outcomes and close the sale. This can't be undone.",
  },
];

// Drawer draws its own panel, which is an overlay modal by default.
// Here it IS the screen: edge to edge, no border, no header, and its own dark
// scrim over the console behind it.
const DRAWER_PASS_THROUGH = {
  root: { style: 'height: 100%; border: 0; background: var(--p-stop-event-drawer-background);' },
  header: { style: 'display: none;' },
  content: { style: 'padding: 0; display: flex; flex-direction: column;' },
  mask: { style: 'background: var(--p-stop-event-drawer-mask-background);' },
};

const choice = ref('pause');
const reason = ref('');
const confirming = ref(false);

const primaryLabel = computed(() => {
  if (choice.value === 'end') return 'Continue';
  return props.pausing ? 'Pausing…' : 'Pause event';
});

// Pause commits straight away; End steps to the confirmation first, and only
// commits from there.
function advance() {
  if (choice.value === 'end') {
    if (!confirming.value) {
      confirming.value = true;
      return;
    }
    emit('end', reason.value.trim());
    return;
  }
  emit('pause', reason.value.trim());
}

// Reopening a sheet that remembered a half-finished decision is how a clerk
// ends a sale they meant to pause.
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    choice.value = 'pause';
    reason.value = '';
    confirming.value = false;
  }
);
</script>

<style scoped>
.stop-event-drawer__inner {
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--p-stop-event-drawer-inner-gap);
  padding: var(--p-stop-event-drawer-inner-padding);
}

.stop-event-drawer__spacer {
  flex: 1;
}

.stop-event-drawer__head {
  display: flex;
  align-items: center;
  gap: var(--p-stop-event-drawer-head-gap);
}

.stop-event-drawer__head-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-stop-event-drawer-head-icon-border-radius);
  background: var(--p-stop-event-drawer-head-icon-background);
  border: var(--p-stop-event-drawer-head-icon-border-width) solid
    var(--p-stop-event-drawer-head-icon-border-color);
  color: var(--p-stop-event-drawer-head-icon-color);
  font-size: var(--p-stop-event-drawer-head-icon-font-size);
}

.stop-event-drawer__head-title {
  font-size: var(--p-stop-event-drawer-head-title-font-size);
  font-weight: var(--p-stop-event-drawer-head-title-font-weight);
  color: var(--p-stop-event-drawer-head-title-color);
}

.p-button.stop-event-drawer__dismiss {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-stop-event-drawer-dismiss-border-radius);
  background: var(--p-stop-event-drawer-dismiss-background);
  border: var(--p-stop-event-drawer-dismiss-border-width) solid var(--p-stop-event-drawer-dismiss-border-color);
  color: var(--p-stop-event-drawer-dismiss-color);
}

.stop-event-drawer__question {
  font-size: var(--p-stop-event-drawer-question-font-size);
  color: var(--p-stop-event-drawer-question-color);
}

/* ── The two choices ────────────────────────────────────────────────────── */

.stop-event-drawer__choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--p-stop-event-drawer-choices-gap);
}

.p-button.stop-event-drawer__choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  font-family: inherit;
  gap: var(--p-stop-event-drawer-choice-gap);
  padding: var(--p-stop-event-drawer-choice-padding);
  border-radius: var(--p-stop-event-drawer-choice-border-radius);
  background: var(--p-stop-event-drawer-choice-background);
  border: var(--p-stop-event-drawer-choice-border-width) solid var(--p-stop-event-drawer-choice-border-color);
  color: var(--p-stop-event-drawer-choice-color);
}

.p-button.stop-event-drawer__choice--pause .stop-event-drawer__choice-icon {
  color: var(--p-stop-event-drawer-pause-color);
}

.p-button.stop-event-drawer__choice--end .stop-event-drawer__choice-icon {
  color: var(--p-stop-event-drawer-end-color);
}

.p-button.stop-event-drawer__choice--pause.stop-event-drawer__choice--selected {
  background: var(--p-stop-event-drawer-pause-selected-background);
  border-color: var(--p-stop-event-drawer-pause-color);
}

.p-button.stop-event-drawer__choice--end.stop-event-drawer__choice--selected {
  background: var(--p-stop-event-drawer-end-selected-background);
  border-color: var(--p-stop-event-drawer-end-color);
}

.stop-event-drawer__choice-icon {
  font-size: var(--p-stop-event-drawer-choice-icon-font-size);
}

.stop-event-drawer__choice-title {
  font-size: var(--p-stop-event-drawer-choice-title-font-size);
  font-weight: var(--p-stop-event-drawer-choice-title-font-weight);
  color: var(--p-stop-event-drawer-choice-title-color);
}

.stop-event-drawer__choice-subtitle {
  font-size: var(--p-stop-event-drawer-choice-subtitle-font-size);
  line-height: var(--p-stop-event-drawer-choice-subtitle-line-height);
  color: var(--p-stop-event-drawer-choice-subtitle-color);
}

/* ── Reason ─────────────────────────────────────────────────────────────── */

.stop-event-drawer__reason {
  display: flex;
  flex-direction: column;
  gap: var(--p-stop-event-drawer-reason-gap);
}

.stop-event-drawer__reason-label {
  font-size: var(--p-stop-event-drawer-reason-label-font-size);
  font-weight: var(--p-stop-event-drawer-reason-label-font-weight);
  color: var(--p-stop-event-drawer-reason-label-color);
}

.stop-event-drawer__reason-optional {
  font-weight: var(--p-stop-event-drawer-reason-optional-font-weight);
  color: var(--p-stop-event-drawer-reason-optional-color);
}

.p-textarea.stop-event-drawer__textarea,
.p-textarea.stop-event-drawer__textarea:enabled:focus {
  width: 100%;
  resize: none;
  outline: none;
  box-shadow: none;
  font-family: inherit;
  font-size: var(--p-stop-event-drawer-textarea-font-size);
  line-height: var(--p-stop-event-drawer-textarea-line-height);
  padding: var(--p-stop-event-drawer-textarea-padding);
  border-radius: var(--p-stop-event-drawer-textarea-border-radius);
  background: var(--p-stop-event-drawer-textarea-background);
  border: var(--p-stop-event-drawer-textarea-border-width) solid var(--p-stop-event-drawer-textarea-border-color);
  color: var(--p-stop-event-drawer-textarea-color);
}

/* ── Confirmation ───────────────────────────────────────────────────────── */

.stop-event-drawer__warning {
  font-size: var(--p-stop-event-drawer-warning-font-size);
  line-height: var(--p-stop-event-drawer-warning-line-height);
  color: var(--p-stop-event-drawer-warning-color);
}

.stop-event-drawer__warning strong {
  color: var(--p-stop-event-drawer-warning-strong-color);
}

.stop-event-drawer__reason-echo {
  padding: var(--p-stop-event-drawer-reason-echo-padding);
  border-radius: var(--p-stop-event-drawer-reason-echo-border-radius);
  background: var(--p-stop-event-drawer-reason-echo-background);
  border: var(--p-stop-event-drawer-reason-echo-border-width) solid
    var(--p-stop-event-drawer-reason-echo-border-color);
  font-size: var(--p-stop-event-drawer-reason-echo-font-size);
  color: var(--p-stop-event-drawer-reason-echo-color);
}

.stop-event-drawer__reason-echo-label {
  margin-right: 8px;
  text-transform: uppercase;
  font-size: var(--p-stop-event-drawer-reason-echo-label-font-size);
  font-weight: var(--p-stop-event-drawer-reason-echo-label-font-weight);
  letter-spacing: var(--p-stop-event-drawer-reason-echo-label-letter-spacing);
  color: var(--p-stop-event-drawer-reason-echo-label-color);
}

/* ── Actions ────────────────────────────────────────────────────────────── */

.stop-event-drawer__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--p-stop-event-drawer-actions-gap);
}

.p-button.stop-event-drawer__cancel {
  font-family: inherit;
  padding: var(--p-stop-event-drawer-cancel-padding);
  border-radius: var(--p-stop-event-drawer-cancel-border-radius);
  font-size: var(--p-stop-event-drawer-cancel-font-size);
  font-weight: var(--p-stop-event-drawer-cancel-font-weight);
  background: var(--p-stop-event-drawer-cancel-background);
  border: var(--p-stop-event-drawer-cancel-border-width) solid var(--p-stop-event-drawer-cancel-border-color);
  color: var(--p-stop-event-drawer-cancel-color);
}

.p-button.stop-event-drawer__primary {
  display: inline-flex;
  align-items: center;
  border: none;
  font-family: inherit;
  padding: var(--p-stop-event-drawer-primary-padding);
  border-radius: var(--p-stop-event-drawer-primary-border-radius);
  font-size: var(--p-stop-event-drawer-primary-font-size);
  font-weight: var(--p-stop-event-drawer-primary-font-weight);
  gap: var(--p-stop-event-drawer-primary-gap);
}

.p-button.stop-event-drawer__primary--pause {
  background: var(--p-stop-event-drawer-pause-color);
  color: var(--p-stop-event-drawer-pause-contrast-color);
}

.p-button.stop-event-drawer__primary--end {
  background: var(--p-stop-event-drawer-end-color);
  color: var(--p-stop-event-drawer-end-contrast-color);
}

.p-button.stop-event-drawer__primary:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
</style>
